import type { CPDSource, SourceAppearance } from '../../contracts';

// ============================================================================
// Shared API Source Types
// ============================================================================

/** A source appearance from the API (snake_case) */
export interface APISourceAppearance {
  chat_id: string;
  chat_title?: string;
  accessed_at?: string;
  estimated_time_minutes?: number;
  is_read?: boolean;
  read_at?: string;
}

/** A source from the API (snake_case) — superset of all endpoint shapes */
export interface APICPDSource {
  id: string;
  url: string;
  title: string;
  domain: string;
  source_name?: string;
  specialty?: string[];
  website_type?: string[];
  icon_url: string;
  total_estimated_time_minutes: number;
  /** Time calculated using the user's time_calculation_method setting.
   *  null/undefined when content_based (same as total_estimated_time_minutes). */
  calculated_time_minutes?: number | null;
  access_count: number;
  /** Whether user has read/opened this source (authoritative from backend) */
  is_read?: boolean;
  first_accessed_at?: string;
  last_accessed_at?: string;
  appearances: APISourceAppearance[];
}

// ============================================================================
// Transform
// ============================================================================

/**
 * Transform an API source (snake_case) to the frontend CPDSource (camelCase).
 *
 * Shared across useCPDSourcesInfinite and useCPDSavedSources
 * to avoid duplicating the same mapping logic.
 */
export function transformSource(apiSource: APICPDSource): CPDSource {
  return {
    id: apiSource.id,
    url: apiSource.url,
    title: apiSource.title,
    domain: apiSource.domain,
    sourceName: apiSource.source_name,
    specialty: apiSource.specialty,
    websiteType: apiSource.website_type,
    iconUrl: apiSource.icon_url,
    estimatedTimeMinutes:
      apiSource.calculated_time_minutes ?? apiSource.total_estimated_time_minutes,
    accessCount: apiSource.access_count,
    isRead: apiSource.is_read ?? false,
    appearances: apiSource.appearances.map(
      (a): SourceAppearance => ({
        chatId: a.chat_id,
        chatTitle: a.chat_title ?? '',
      })
    ),
    firstAccessedAt: apiSource.first_accessed_at,
    lastAccessedAt: apiSource.last_accessed_at,
  };
}
